import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import { Button, CircularProgress, Grid } from "@mui/material";
import { useFormik } from "formik";
import { omit } from "lodash";
import * as Yup from "yup";
import PropTypes from "prop-types";

import InputFieldText from "Components/Common/InputFieldText";
import Modal from "Components/Common/Modal";
import {
  SUPER_ADMIN,
  dcsAdmin2Roles,
  dcsAdmin3Roles,
  otherUtilitiesAdmin2Roles,
  otherUtilitiesAdmin3Roles,
} from "common/constant";
import { Text } from "Components/MeterInfoForm/ConsumerDetails/styles";
import { getOptionValue } from "common/enums";
import Backdrop from "Components/Common/Backdrop";

import { filterEmptyStrings, isAuthorizedUser } from "utils";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

import {
  useGetApplicationRefundCalculations,
  usePostRefundCalculationMutation,
  usePutRefundCalculationMutation,
} from "apis/ApplicationRequestDetail";

const RefundCalculationForm = ({
  handleClose,
  t,
  utilityConsumerId,
  utilityId,
  applicationId,
}) => {
  const { roles } = useSelector((state) => state.Login);

  const {
    data: { data: refundCalculationsData } = {},
    isLoading: isRefundCalculationDataLoading,
  } = useGetApplicationRefundCalculations(
    filterEmptyStrings({
      applicationId,
      utilityConsumerId,
    })
  );

  const [addRefundCalculations, { isLoading }] =
    usePostRefundCalculationMutation();

  const [updateRefundCalculations, { isLoading: isRefundCalculationUpdating }] =
    usePutRefundCalculationMutation();

  const isDCSUtility = useMemo(
    () => utilityId === getOptionValue("utilityOptions", "DCS"),
    [utilityId]
  );
  const isFinanceAdmin = useMemo(
    () =>
      isAuthorizedUser(roles, [
        ...dcsAdmin2Roles,
        ...otherUtilitiesAdmin2Roles,
      ]),
    [roles]
  );

  const {
    consumerNumber,
    consumerName,
    address,
    dateOfConnection,
    dateOfDisconnection,
    securityDeposit: utilityWiseSecurityDeposit,
  } = refundCalculationsData?.utilityConsumerDetails || {};

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      applicationId,
      utilityConsumerId,
      refundCalculation: {
        id: refundCalculationsData?.refundCalculation?.id ?? 0,
        securityDeposit:
          refundCalculationsData?.refundCalculation?.securityDeposit ||
          utilityWiseSecurityDeposit ||
          0,
        depreciatedCostOfRecoveredMaterial:
          refundCalculationsData?.refundCalculation
            ?.depreciatedCostOfRecoveredMaterial ?? 0,
        materialAndLabourCharges:
          refundCalculationsData?.refundCalculation?.materialAndLabourCharges ??
          0,
        disconnectionCharge:
          refundCalculationsData?.refundCalculation?.disconnectionCharge ?? 0,
        superVisionCharges:
          refundCalculationsData?.refundCalculation?.superVisionCharges ?? 0,
        pendingDues:
          refundCalculationsData?.refundCalculation?.pendingDues ?? 0,
        finalAmount:
          refundCalculationsData?.refundCalculation?.finalAmount ?? 0,
        GST: refundCalculationsData?.refundCalculation?.GST ?? 0,
      },
    },
    validationSchema: Yup.object({
      refundCalculation: Yup.object({
        securityDeposit: Yup.number().required("Security deposit is required"),
        materialAndLabourCharges: Yup.number().required(
          "Miscellaneous charges required"
        ),
        ...(isDCSUtility && {
          disconnectionCharge: Yup.number().required(
            "Disconnection charges required"
          ),
          depreciatedCostOfRecoveredMaterial: Yup.number().required(
            "Depreciated value is required"
          ),
        }),
        pendingDues: Yup.number(),
      }),
    }),
    onSubmit: async (values) => {
      const payload = omit(filterEmptyStrings(values), [
        "refundCalculation.GST",
        "refundCalculation.finalAmount",
        "refundCalculation.superVisionCharges",
      ]);
      try {
        if (roles.includes(SUPER_ADMIN)) {
          await updateRefundCalculations(payload);
        } else {
          await addRefundCalculations(payload);
        }
        handleClose();
      } catch (error) {}
    },
  });
  const {
    handleSubmit,
    setFieldValue,
    values: {
      refundCalculation: {
        securityDeposit,
        materialAndLabourCharges,
        depreciatedCostOfRecoveredMaterial,
        disconnectionCharge,
        pendingDues,
        superVisionCharges,
        GST,
      },
    },
  } = formik;

  useEffect(() => {
    if (utilityId === getOptionValue("utilityOptions", "DCS")) {
      setFieldValue(
        "refundCalculation.finalAmount",
        +(
          securityDeposit +
          depreciatedCostOfRecoveredMaterial -
          materialAndLabourCharges -
          disconnectionCharge -
          pendingDues
        ).toFixed(2)
      );
    }

    if (
      [
        getOptionValue("utilityOptions", "Water"),
        getOptionValue("utilityOptions", "Sewage"),
        getOptionValue("utilityOptions", "Solid Waste"),
      ].includes(utilityId)
    ) {
      setFieldValue(
        "refundCalculation.finalAmount",
        +(
          securityDeposit -
          materialAndLabourCharges -
          superVisionCharges -
          GST -
          pendingDues
        ).toFixed(2)
      );
    }
  }, [
    securityDeposit,
    materialAndLabourCharges,
    depreciatedCostOfRecoveredMaterial,
    disconnectionCharge,
    GST,
    superVisionCharges,
    pendingDues,
  ]);

  useEffect(() => {
    setFieldValue(
      "refundCalculation.superVisionCharges",
      materialAndLabourCharges * 0.15
    );
  }, [materialAndLabourCharges]);

  useEffect(() => {
    setFieldValue(
      "refundCalculation.GST",
      (materialAndLabourCharges + superVisionCharges) * 0.18
    );
  }, [materialAndLabourCharges, superVisionCharges]);

  if (isRefundCalculationDataLoading) {
    return <Backdrop open />;
  }

  return (
    <Modal
      isOpen
      handleClose={handleClose}
      title={t("Refund Calculation")}
      maxWidth="md"
    >
      <Grid
        container
        columnSpacing={1}
        className="border border-2 ps-2 pb-2 p-3 mb-2 mt-2"
      >
        <Grid container columnSpacing={4}>
          <Grid item xs={12} sm={6}>
            <Text>
              <b>{t("Consumer Number")}: </b>
              {consumerNumber}
            </Text>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Text>
              <b>{t("Consumer Name")}: </b>
              {consumerName}
            </Text>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Text>
              <b>{t("Date Of Connection")}: </b>
              {dateOfConnection
                ? convertUTCDateToLocalDate(dateOfConnection).format(
                    "DD-MM-YYYY"
                  )
                : "--"}
            </Text>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Text>
              <b>{t("Date Of Disconnection")}: </b>
              {dateOfDisconnection
                ? convertUTCDateToLocalDate(dateOfDisconnection).format(
                    "DD-MM-YYYY"
                  )
                : "--"}
            </Text>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Text>
              <b>{t("Address")}: </b>
              {address}
            </Text>
          </Grid>
        </Grid>
      </Grid>
      <Grid container className="mt-3" columnSpacing={2}>
        <Grid item xs={12} sm={6}>
          <InputFieldText
            label={t("Security Deposit") + "*"}
            name="refundCalculation.securityDeposit"
            formik={formik}
            disabled={isFinanceAdmin}
            type="number"
            placeholder="Enter Security Deposit"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputFieldText
            label={t("Miscellaneous and Labour charges") + "*"}
            name="refundCalculation.materialAndLabourCharges"
            formik={formik}
            disabled={isFinanceAdmin}
            type="number"
            placeholder="Enter Miscellaneous and Labour charges"
          />
        </Grid>
        {isDCSUtility && (
          <>
            <Grid item xs={12} sm={6}>
              <InputFieldText
                label={t("Depreciated value") + "*"}
                name="refundCalculation.depreciatedCostOfRecoveredMaterial"
                formik={formik}
                disabled={isFinanceAdmin}
                type="number"
                placeholder="Enter Depreciated value"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputFieldText
                label={t("Disconnection Charges") + "*"}
                name="refundCalculation.disconnectionCharge"
                formik={formik}
                disabled={isFinanceAdmin}
                type="number"
                placeholder="Enter Disconnection Charges"
              />
            </Grid>
          </>
        )}

        {!isDCSUtility && (
          <>
            <Grid item xs={12} sm={6}>
              <InputFieldText
                label={t("Supervision Charges") + "*"}
                name="refundCalculation.superVisionCharges"
                formik={formik}
                disabled
                type="number"
                placeholder="Enter Supervision Charges"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <InputFieldText
                label={t("GST value") + "*"}
                name="refundCalculation.GST"
                disabled
                formik={formik}
                type="number"
                placeholder="Enter GST value"
              />
            </Grid>
          </>
        )}
        <Grid item xs={12} sm={6}>
          <InputFieldText
            label={t("Pending Dues")}
            name="refundCalculation.pendingDues"
            formik={formik}
            disabled={isAuthorizedUser(roles, [
              ...dcsAdmin3Roles,
              ...otherUtilitiesAdmin3Roles,
            ])}
            type="number"
            placeholder="Enter Pending Dues"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputFieldText
            label={t("Final Amount") + "*"}
            name="refundCalculation.finalAmount"
            disabled
            formik={formik}
            type="number"
            placeholder="Enter Final Amount"
          />
        </Grid>
      </Grid>
      <Grid className="d-flex justify-content-end" mt={1}>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          disabled={isLoading || isRefundCalculationUpdating}
          onClick={handleSubmit}
        >
          Submit
          {(isLoading || isRefundCalculationUpdating) && (
            <CircularProgress size={20} />
          )}
        </Button>
      </Grid>
    </Modal>
  );
};
RefundCalculationForm.propTypes = {
  applicationId: PropTypes.number,
  utilityId: PropTypes.number,
  utilityConsumerId: PropTypes.number,
  handleClose: PropTypes.func,
  t: PropTypes.func,
};

export default withTranslation()(RefundCalculationForm);
